import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentMethodsEnum } from "@zenith/features/Payments/types";

import Button from "@mui/material/Button";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { NQRIcon, Scan } from "@zenith/assets/icons/components";
import {
  DEFAULT_CHANNEL,
  DEFAULT_CURRENCY,
  EXPIRATION_DATE_FOR_PAYMENTS,
  EXPIRATION_DATE_FOR_PENDING_QR_CODE_ITEM,
  EXPIRATION_DATE_FOR_QR_CODE_ITEM,
  MILLISECONDS,
} from "@zenith/data/constants";
import { usePaymentsContext } from "@zenith/context/global-context/hooks";
import { formatCurrency, formatTimeForCounter } from "@zenith/utils";
import {
  OperationDTO,
  OperationsApiService,
  PayOnlyOperationReqBaseDTO,
} from "@zenith/services/transactions";
import {
  PAYMENT_OVERVIEW,
  PAYMENT_QR_CODE,
  PAYMENT_QR_CODE_PENDING,
} from "@zenith/routes/paths";
import { pollPaymentStatus } from "@zenith/utils/Polls/pollPaymentStatus";
import { SpinnerComponent, ToastSnackbarComponent } from "@zenith/components";
import { useNotificationContext } from "@zenith/context";
import WalletIcon from "@zenith/assets/icons/WalletIcon";

const OPERATION_ID_STORAGE_KEY = "qr_code_operation_id";
const TOTAL_AMOUNT_STORAGE_KEY = "qr_code_total_amount";

const QRCodePage = memo(function QRCodePage() {
  const { paymentsContext, setPaymentsContext } = usePaymentsContext();
  const {
    totalAmount,
    amount,
    expirationDate,
    operationId,
    transactionInfo: { qrCode },
  } = paymentsContext;
  const navigate = useNavigate();
  const { setOpen } = useNotificationContext();

  const [isLoadingNewCode, setIsLoadingNewCode] = useState(false);
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({ title: "", message: "", status: "error" });
  const [seconds, setSeconds] = useState<number>(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (expirationDate) {
      const initialRemainingSeconds = Math.floor(
        (new Date(expirationDate).getTime() - new Date().getTime()) /
          MILLISECONDS
      );
      setSeconds(initialRemainingSeconds);
    }
  }, [expirationDate]);

  useEffect(() => {
    if (!operationId || !qrCode) {
      const storedOperationId = localStorage.getItem(OPERATION_ID_STORAGE_KEY);
      const storedTotalAmount = localStorage.getItem(TOTAL_AMOUNT_STORAGE_KEY);
      const storedQRCode = localStorage.getItem("qr_code");
      const storedExpirationDate = localStorage.getItem("qr_expiration_date");

      if (storedExpirationDate) {
        const expirationTime = new Date(storedExpirationDate).getTime();
        const now = new Date().getTime();

        if (expirationTime > now && storedQRCode && storedOperationId) {
          setPaymentsContext({
            ...paymentsContext,
            operationId: storedOperationId,
            totalAmount: storedTotalAmount
              ? parseFloat(storedTotalAmount)
              : totalAmount,
            expirationDate: storedExpirationDate,
            transactionInfo: {
              ...paymentsContext.transactionInfo,
              qrCode: storedQRCode,
            },
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (qrCode && operationId && expirationDate) {
      localStorage.setItem("qr_code", qrCode);
      localStorage.setItem("qr_expiration_date", expirationDate);
    }
  }, [qrCode, operationId, expirationDate]);

  useEffect(() => {
    if (operationId) {
      localStorage.setItem(OPERATION_ID_STORAGE_KEY, operationId);
    }
    if (totalAmount) {
      localStorage.setItem(TOTAL_AMOUNT_STORAGE_KEY, totalAmount.toString());
    }
  }, [operationId, totalAmount]);

  useEffect(() => {
    if (!expirationDate) return;

    const timer = setInterval(() => {
      const remaining =
        new Date(expirationDate).getTime() - new Date().getTime();

      if (remaining >= 0) {
        setSeconds(Math.floor(remaining / MILLISECONDS));
      } else {
        clearInterval(timer);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [expirationDate]);

  const handleGenerateNewQR = async () => {
    if (isLoadingNewCode) return;
    setIsLoadingNewCode(true);

    try {
      const currentAmount = amount || totalAmount;
      const orderItems = paymentsContext.orderItems;
      const hasCartItems =
        (orderItems?.length ?? 0) > 0 && (currentAmount ?? 0) > 0;

      if (!currentAmount) {
        throw new Error("Amount is missing");
      }

      const response = hasCartItems
        ? await OperationsApiService.postOperationQrCode(DEFAULT_CHANNEL, {
            currency: DEFAULT_CURRENCY,
            saleItemDetail: (orderItems ?? []).map((item) => ({
              amountPerUnit:
                parseFloat(item.price.replace(/[^0-9.]/g, "")) || 0,
              description: item.name || "",
              quantity: item.quantity ? Number(item.quantity) : 1,
              saleItemId: item.id?.toString() || "0",
            })),
          })
        : await OperationsApiService.postOperationQrCode1(DEFAULT_CHANNEL, {
            amount: currentAmount,
            currency: DEFAULT_CURRENCY,
            description: "Manual amount payment",
          } as PayOnlyOperationReqBaseDTO);

      if (response?.data?.operationId) {
        const newOperationId = response.data.operationId;
        const newExpirationDate = response.data?.qrcode?.expirationDate || "";
        const newQrCode = response.data?.qrcode?.operationCode || "";

        setPaymentsContext({
          ...paymentsContext,
          expirationDate: newExpirationDate,
          operationId: newOperationId,
          totalAmount: response.data?.totalAmount ?? currentAmount,
          paymentMethod: PaymentMethodsEnum.QR_CODE_PAYMENT,
          transactionInfo: {
            ...paymentsContext.transactionInfo,
            transactionDate: response.dateTime || "",
            paymentMethod: OperationDTO.paymentMethod.QR_CODE,
            qrCode: newQrCode,
          },
        });

        localStorage.setItem(OPERATION_ID_STORAGE_KEY, newOperationId);
        localStorage.setItem(
          TOTAL_AMOUNT_STORAGE_KEY,
          currentAmount.toString()
        );
        localStorage.setItem("qr_code", newQrCode);
        localStorage.setItem("qr_expiration_date", newExpirationDate);
        localStorage.setItem(
          "qr_state",
          JSON.stringify({
            operationId: newOperationId,
            expirationDate: newExpirationDate,
            paymentMethod: PaymentMethodsEnum.QR_CODE_PAYMENT,
            operationPaymentMethod: OperationDTO.paymentMethod.QR_CODE,
            qrCode: newQrCode,
            totalAmount: currentAmount,
            timestamp: Date.now(),
          })
        );

        setIsSuccess(false);
      }
    } catch (error: any) {
      setToasterData({
        title: "Error",
        message:
          error?.body?.message ||
          error?.message ||
          "Failed to generate QR code",
        status: "error",
      });
      setOpen(true);
    } finally {
      setIsLoadingNewCode(false);
    }
  };

  useEffect(() => {
    const storedState = localStorage.getItem("qr_state");
    if (storedState) {
      try {
        const {
          operationId: storedOpId,
          expirationDate: storedExpDate,
          paymentMethod,
          operationPaymentMethod,
          qrCode: storedQRCode,
          totalAmount: storedAmount,
          timestamp,
        } = JSON.parse(storedState);

        const now = Date.now();
        const expTime = new Date(storedExpDate).getTime();

        if (expTime > now) {
          setPaymentsContext({
            ...paymentsContext,
            operationId: storedOpId,
            expirationDate: storedExpDate,
            totalAmount: storedAmount,
            paymentMethod: paymentMethod as PaymentMethodsEnum,
            transactionInfo: {
              ...paymentsContext.transactionInfo,
              qrCode: storedQRCode,
              paymentMethod:
                operationPaymentMethod as OperationDTO.paymentMethod,
            },
          });

          const remainingSeconds = Math.floor((expTime - now) / MILLISECONDS);
          setSeconds(remainingSeconds);

          if (remainingSeconds <= 0) {
            localStorage.removeItem("qr_state");
          }
        }
      } catch (e) {
        console.error("[QRCodePage] Error restoring state:", e);
        localStorage.removeItem("qr_state");
      }
    }
  }, []);

  function handleBack() {
    localStorage.removeItem(EXPIRATION_DATE_FOR_QR_CODE_ITEM);
    localStorage.removeItem(OPERATION_ID_STORAGE_KEY);
    localStorage.removeItem(TOTAL_AMOUNT_STORAGE_KEY);
    localStorage.removeItem("qr_state");
    localStorage.removeItem("qr_code");
    localStorage.removeItem("qr_expiration_date");
    setPaymentsContext({
      ...paymentsContext,
      operationId: undefined,
      expirationDate: undefined,
      transactionInfo: {
        ...paymentsContext.transactionInfo,
        qrCode: undefined,
      },
    });
    navigate(PAYMENT_OVERVIEW, { state: { prevPath: PAYMENT_QR_CODE } });
  }

  const handleConfirmPayment = async () => {
    if (seconds <= 0 || !operationId) return;

    setIsConfirming(true);
    try {
      const response = await pollPaymentStatus(operationId, false);

      setPaymentsContext({
        ...paymentsContext,
        ...response.data,
        paymentStatus: response.status,
        operationDetails: response.data,
      });

      if (response.status === OperationDTO.status.SUCCESS) {
        localStorage.removeItem(EXPIRATION_DATE_FOR_PENDING_QR_CODE_ITEM);
        localStorage.setItem("paymentStatus", OperationDTO.status.SUCCESS);

        navigate(PAYMENT_QR_CODE_PENDING, {
          state: {
            prevPath: PAYMENT_QR_CODE,
            initialStatus: OperationDTO.status.SUCCESS,
          },
        });
      } else {
        const expirationDateForQr = Date.now() + EXPIRATION_DATE_FOR_PAYMENTS;
        localStorage.setItem(
          EXPIRATION_DATE_FOR_PENDING_QR_CODE_ITEM,
          expirationDateForQr.toString()
        );

        navigate(PAYMENT_QR_CODE_PENDING, {
          state: {
            prevPath: PAYMENT_QR_CODE,
            initialStatus: response.status,
            errorMessage: response.error?.message,
          },
        });
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <>
      <div>
        <Button
          id="back-qr-link"
          onClick={handleBack}
          className="text-base font-normal normal-case text-neutral-600 hover:bg-white"
        >
          <ArrowBackIosIcon fontSize="small" /> Back
        </Button>

        <div className="mt-12 mb-16 flex justify-center">
          <div className="flex w-120 flex-col rounded-2.5xl border border-neutral-200 px-8 pt-6 pb-8">
            <h1 className="mb-4 text-2xl font-medium text-neutral-900">
              QR Code
            </h1>
            <p className="mb-12 text-sm font-normal text-neutral-500">
              {seconds > 0
                ? "Allow your customer to scan the code to receive payment"
                : ""}
            </p>
            <hr className="mb-8 border-neutral-200"></hr>
            <div className="mb-8 flex justify-between">
              <h2 className="text-base font-normal text-neutral-400">Amount</h2>
              <h2 className="text-main-200-925 text-2xl font-medium">
                {paymentsContext.orderItems &&
                paymentsContext.orderItems.length === 0
                  ? formatCurrency(amount)
                  : formatCurrency(totalAmount)}
              </h2>
            </div>
            {seconds > 0 ? (
              <div className="flex flex-col items-center justify-center">
                <div className="relative">
                  <img
                    src={`data:image/png;base64,${qrCode}`}
                    alt="QR Code"
                    width={217}
                    height={217}
                    style={{ objectFit: "contain" }}
                  />
                  <div className="absolute top-1/2 left-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-white">
                    <WalletIcon />
                  </div>
                </div>

                <div className="mt-6 mb-8 flex w-[178px] items-center justify-between">
                  <NQRIcon />
                </div>
              </div>
            ) : (
              <div className="flex h-[358px] flex-col items-center justify-center">
                <div className="mb-6 flex h-[120px] w-[120px] items-center justify-center rounded-full bg-primary-300/50">
                  <Scan width={64} height={64} />
                </div>
                <p className="text-neutral-600">The QR code has expired</p>
              </div>
            )}
            <div className="flex flex-col items-center">
              {seconds > 0 ? (
                <p className="mb-8 text-sm font-medium text-neutral-600">
                  The above QR code is valid for{" "}
                  <span className="text-neutral-900">
                    {formatTimeForCounter(seconds)}
                  </span>
                </p>
              ) : (
                <div className="mb-8">
                  <Button
                    variant="text"
                    id="clear-cart-button"
                    className={`text-base font-normal normal-case ${
                      isLoadingNewCode
                        ? "cursor-not-allowed text-neutral-200"
                        : "text-neutral-600 hover:text-neutral-900"
                    } underline hover:bg-white hover:underline`}
                    disableRipple={isLoadingNewCode}
                    onClick={handleGenerateNewQR}
                  >
                    Get New QR Code
                  </Button>
                </div>
              )}
              <Button
                className={`w-[212px] rounded-lg px-8 py-3 text-base font-medium normal-case ${
                  seconds > 0
                    ? "bg-primary-300 text-white hover:bg-primary-300"
                    : "cursor-not-allowed bg-neutral-200 text-neutral-200 hover:bg-neutral-200"
                }`}
                onClick={handleConfirmPayment}
                disabled={seconds <= 0 || isConfirming}
              >
                {isConfirming ? (
                  <SpinnerComponent size={32} type={"secondary"} />
                ) : (
                  "Confirm Payment"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ToastSnackbarComponent
        horizontal="center"
        vertical="top"
        status={toasterData.status}
        title={toasterData.title}
        text={toasterData.message}
        size="lg"
        hasAutoHideDuration
      />
    </>
  );
});

export default QRCodePage;
